import {
    GA_SALES_STATUS_BUTTON_DIMENSION,
    GA_SALES_STATUS_CATEGORY_EVENT_STATUS,
} from '@eb/event-create-analytics';
import {
    trackAnalyticsEvent,
    trackEventFromStateOnPageUnload,
    trackEventFromState,
} from '@eb/site-analytics';
import { formatForAPI } from '@eb/datetime';
import gettext from '@eb/gettext';
import { logCheckpointViaAjax } from '@eb/janus';
import { setWindowLocation } from '@eb/http';
import { eventCache } from './api';

import {
    ACTION_EVENT_PUBLISH,
    ACTION_FIRST_PAID_PUBLISH,
    ACTION_FIRST_PUBLISH,
    CREATE_EVENT_PUBLISH,
    DISPLAY_SALES_STATUS_CUSTOM,
    DISPLAY_SALES_STATUS_POSTPONED,
    FIRST_CREATE_EVENT_PAID_PUBLISH,
    FIRST_CREATE_EVENT_PUBLISH,
    GA_GOAL_CATEGORY,
    GA_SALES_STATUS_ACTION_POSTPONE_CONFIRMED,
    GA_TRACKING_EVENT_CATEGORY,
    LABEL_NEW_CREATE,
} from '../constants';
import {
    cancelEvent,
    cancelSchedulePublishErrorSpec,
    deleteEvent,
    endOnlineSales as endOnlineSalesAPI,
    endOnlineSalesErrorSpec,
    publishEvent,
    savePublishSettings,
    saveSalesStatus as saveSalesStatusAPI,
    schedulePublishErrorSpec,
    unpublishEvent,
} from './api';
import {
    cancelSchedulePublishFailure,
    endOnlineSalesFailure,
    eventCancelFailure,
    eventCancelStart,
    eventCancelSuccess,
    eventDeleteFailure,
    eventDeleteStart,
    eventDeleteSuccess,
    eventPublishFailure,
    eventPublishStart,
    eventUnpublishFailure,
    eventUnpublishStart,
    eventUnpublishSuccess,
    saveSalesStatusFailure,
    saveSalesStatusStart,
    saveSalesStatusSuccess,
} from './actions';
import {
    eventIsFree,
    formatSalesStatusData,
    getErrorStringFromTranslatedError,
    getRedirectURL,
    gtmTrackingPixelEvent,
} from './utils';

export const GTM_PIXEL_FIRST_PUBLISH = 'First Publish';
export const GTM_PIXEL_INCREMENTAL_PUBLISH = 'Incremental Publish';
export const GTM_PIXEL_FIRST_PAID_PUBLISH = 'First Paid Publish';

/**
 * Wraps a call to `publishEvent` with action dispatches
 */
export const dispatchPublishEvent = ({ eventId, ...extraProps }) => (
    dispatch,
    getState,
) => {
    const state = getState();
    const event = (eventId && eventCache.getResource(state, { eventId })) || {};
    const isPaidEvent = !eventIsFree(event);
    const { hasPublish, hasPaidPublish } = state;
    dispatch(eventPublishStart());

    return publishEvent(eventId)
        .then(() => {
            const eventCheckpointPromises = [];

            trackAnalyticsEvent({
                action: ACTION_EVENT_PUBLISH,
                category: GA_TRACKING_EVENT_CATEGORY,
                label: LABEL_NEW_CREATE,
                onWindowUnload: true,
                dimensions: { eventId },
                ...extraProps,
            });

            if (!hasPublish) {
                trackAnalyticsEvent({
                    action: ACTION_FIRST_PUBLISH,
                    category: GA_GOAL_CATEGORY,
                    label: LABEL_NEW_CREATE,
                    onWindowUnload: true,
                    eventId,
                });
                gtmTrackingPixelEvent(GTM_PIXEL_FIRST_PUBLISH);

                eventCheckpointPromises.push(
                    logCheckpointViaAjax(FIRST_CREATE_EVENT_PUBLISH, {
                        event_id: eventId,
                    }),
                );
            } else {
                gtmTrackingPixelEvent(GTM_PIXEL_INCREMENTAL_PUBLISH);
            }

            if (isPaidEvent && !hasPaidPublish) {
                trackAnalyticsEvent({
                    action: ACTION_FIRST_PAID_PUBLISH,
                    category: GA_GOAL_CATEGORY,
                    label: LABEL_NEW_CREATE,
                    onWindowUnload: true,
                    eventId,
                });
                gtmTrackingPixelEvent(GTM_PIXEL_FIRST_PAID_PUBLISH);
                eventCheckpointPromises.push(
                    logCheckpointViaAjax(FIRST_CREATE_EVENT_PAID_PUBLISH, {
                        event_id: eventId,
                    }),
                );
            }

            eventCheckpointPromises.push(
                logCheckpointViaAjax(CREATE_EVENT_PUBLISH, {
                    event_id: eventId,
                }),
            );

            Promise.all(eventCheckpointPromises).finally(() => {
                setWindowLocation(getRedirectURL(eventId));
            });
        })
        .catch((error) => {
            dispatch(
                eventPublishFailure(
                    getErrorStringFromTranslatedError(
                        error,
                        gettext(
                            'There has been a problem with your fetch operation: %(message)s',
                            { message: error.message },
                        ),
                    ),
                    error,
                ),
            );
        });
};

/**
 * Wraps a call to `unpublishEvent` with action dispatches
 */
export const dispatchUnpublishEvent = (dispatch, eventId) => {
    dispatch(eventUnpublishStart());

    return unpublishEvent(eventId)
        .then((result) => {
            dispatch(eventUnpublishSuccess(result));
        })
        .catch((error) => {
            dispatch(
                eventUnpublishFailure(
                    getErrorStringFromTranslatedError(
                        error,
                        gettext(
                            'There has been a problem with your fetch operation: %(message)s',
                            { message: error.message },
                        ),
                    ),
                    error,
                ),
            );
        });
};

/**
 * Wraps a call to `cancelEvent` with action dispatches
 */
export const dispatchCancelEvent = (dispatch, eventId) => {
    dispatch(eventCancelStart());

    return cancelEvent(eventId)
        .then((result) => {
            dispatch(eventCancelSuccess(result));
        })
        .catch((error) => {
            dispatch(
                eventCancelFailure(
                    getErrorStringFromTranslatedError(
                        error,
                        gettext(
                            'There has been a problem with your fetch operation: %(message)s',
                            { message: error.message },
                        ),
                    ),
                    error,
                ),
            );
        });
};

/**
 * Wraps a call to `deleteEvent` with action dispatches
 */
export const dispatchDeleteEvent = (dispatch, eventId) => {
    dispatch(eventDeleteStart());

    return deleteEvent(eventId)
        .then((result) => {
            dispatch(eventDeleteSuccess(result));
            setWindowLocation('/myevents');
        })
        .catch((error) => {
            dispatch(
                eventDeleteFailure(
                    getErrorStringFromTranslatedError(
                        error,
                        gettext(
                            'There has been a problem with your fetch operation: %(message)s',
                            { message: error.message },
                        ),
                    ),
                    error,
                ),
            );
        });
};

export const dispatchCancelSchedulePublish = (dispatch, eventId) => {
    const data = {
        publish_settings: {
            schedule_publish_date: null,
        },
    };

    return savePublishSettings(
        data,
        cancelSchedulePublishErrorSpec,
        eventId,
    ).catch((error) => {
        dispatch(
            cancelSchedulePublishFailure(
                getErrorStringFromTranslatedError(error),
            ),
        );
    });
};

export const schedulePublishEvent = (
    eventId,
    startDate,
    startTime,
    timeZone,
) => {
    const publishDate = formatForAPI(startDate, startTime, timeZone);
    const schedulePublishBody = {
        publish_settings: {
            schedule_publish_date: publishDate,
        },
    };

    return savePublishSettings(
        schedulePublishBody,
        schedulePublishErrorSpec,
        eventId,
    );
};

const saveSalesStatus = (data, eventId, source, state, dispatch) => {
    const eventTextKey = Object.keys(data.texts)[0];

    return saveSalesStatusAPI(data, eventId, eventTextKey)
        .then((result) => dispatch(saveSalesStatusSuccess(result)))
        .then(() => {
            const messageCode = data?.texts?.[eventTextKey]?.message_code;
            if (messageCode === DISPLAY_SALES_STATUS_POSTPONED) {
                trackEventFromStateOnPageUnload(state, {
                    action: GA_SALES_STATUS_ACTION_POSTPONE_CONFIRMED,
                    category: GA_SALES_STATUS_CATEGORY_EVENT_STATUS,
                    dimensions: {
                        nameOfButton: `postpone_event_status_confirmed_${source}`,
                    },
                });
            }
        })
        .catch((error) => {
            dispatch(
                saveSalesStatusFailure(
                    getErrorStringFromTranslatedError(error),
                ),
            );
        });
};

export const changeSalesStatus = (
    eventId,
    values,
    source = GA_SALES_STATUS_BUTTON_DIMENSION,
) => (dispatch, getState) => {
    dispatch(saveSalesStatusStart);

    const { statusCode, customMessage, eventSalesStatus } = values;
    const state = getState();

    let isNotYetOnSale;
    let isSoldOut;

    if (!eventSalesStatus) {
        ({
            eventSalesStatus: { isNotYetOnSale, isSoldOut },
        } = state);
    } else {
        ({ isNotYetOnSale, isSoldOut } = eventSalesStatus);
    }

    const data = formatSalesStatusData({
        statusCode,
        customMessage,
        isNotYetOnSale,
        isSoldOut,
    });

    // If the event is not yet on sale and the user selects custom status
    // or the event is sold out, we don't end online ticket sales.
    if (
        (isNotYetOnSale && statusCode === DISPLAY_SALES_STATUS_CUSTOM) ||
        isSoldOut
    ) {
        return saveSalesStatus(data, eventId, source, state, dispatch);
    }

    // By default we end online ticket sales.
    return endOnlineSalesAPI(eventId).then(({ response }) => {
        if (response.ok) {
            return saveSalesStatus(data, eventId, source, state, dispatch);
        }
        dispatch(
            endOnlineSalesFailure(
                endOnlineSalesErrorSpec[response.statusText] ||
                    endOnlineSalesErrorSpec.DEFAULT,
            ),
        );

        return Promise.reject();
    });
};

export const getPostponeStartButtonNameDimension = (source) =>
    `postpone_event_status_started_${source}`;

export const trackEventThunk = (params) => (_, getState) => {
    trackEventFromState(getState(), params);
};
